import { type LucideIcon, Search, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { EntityIcon } from '@/components/knowledge-graph/EntityIcon';
import type { CanonicalEntity } from '@/hooks/useEntities';

interface EntityPickerProps {
  candidates: CanonicalEntity[];
  onPick: (entity: CanonicalEntity) => void;
  triggerLabel: string;
  triggerIcon: LucideIcon;
  searchPlaceholder: string;
  showEntityIcon?: boolean;
  emptyMessage?: string;
  maxResults?: number;
  maxListHeight?: 'sm' | 'md';
  portalContainer?: HTMLElement | null;
}

type PickerDropdownPosition = {
  left: number;
  width: number;
  maxHeight: number;
  fixed: boolean;
  top?: number;
  bottom?: number;
};

export function EntityPicker({
  candidates,
  onPick,
  triggerLabel,
  triggerIcon: TriggerIcon,
  searchPlaceholder,
  showEntityIcon = false,
  emptyMessage,
  maxResults = Number.POSITIVE_INFINITY,
  maxListHeight = 'sm',
  portalContainer,
}: EntityPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState<PickerDropdownPosition | null>(null);

  const resolvedContainer = portalContainer ?? (typeof document !== 'undefined' ? document.body : null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matches = q
      ? candidates.filter(e =>
        e.name.toLowerCase().includes(q) ||
        e.aliases.some(a => a.toLowerCase().includes(q))
      )
      : candidates;

    return Number.isFinite(maxResults)
      ? matches.slice(0, maxResults)
      : matches;
  }, [candidates, search, maxResults]);

  const close = () => { setOpen(false); setSearch(''); };
  const preferredMaxHeight = maxListHeight === 'md' ? 192 : 160;

  const updateDropdownPosition = () => {
    const trigger = triggerRef.current;
    if (!trigger) {
      setDropdownPosition(null);
      return;
    }

    const triggerRect = trigger.getBoundingClientRect();
    const hasCustomContainer = !!portalContainer;
    const containerRect = hasCustomContainer
      ? portalContainer.getBoundingClientRect()
      : {
          left: 0,
          top: 0,
          right: window.innerWidth,
          bottom: window.innerHeight,
          width: window.innerWidth,
          height: window.innerHeight,
        };
    const padding = 12;
    const width = Math.min(512, Math.max(280, containerRect.width - padding * 2));
    const left = Math.min(
      Math.max(triggerRect.left - containerRect.left, padding),
      Math.max(padding, containerRect.width - width - padding),
    );
    const spaceBelow = containerRect.bottom - triggerRect.bottom - padding;
    const spaceAbove = triggerRect.top - containerRect.top - padding;
    const placeBelow = spaceBelow >= 160 || spaceBelow >= spaceAbove;
    const maxHeight = Math.max(
      96,
      Math.min(preferredMaxHeight, (placeBelow ? spaceBelow : spaceAbove) - 8),
    );

    setDropdownPosition({
      left,
      width,
      maxHeight,
      fixed: !hasCustomContainer,
      ...(placeBelow
        ? { top: triggerRect.bottom - containerRect.top + 6 }
        : { bottom: containerRect.bottom - triggerRect.top + 6 }),
    });
  };

  useEffect(() => {
    if (!open) {
      setDropdownPosition(null);
      return;
    }

    updateDropdownPosition();
    const handleLayoutChange = () => updateDropdownPosition();
    window.addEventListener('resize', handleLayoutChange);
    window.addEventListener('scroll', handleLayoutChange, true);
    return () => {
      window.removeEventListener('resize', handleLayoutChange);
      window.removeEventListener('scroll', handleLayoutChange, true);
    };
  }, [open, portalContainer, filtered.length, preferredMaxHeight]);

  useEffect(() => {
    if (!open || !dropdownPosition) return;
    inputRef.current?.focus();
  }, [open, dropdownPosition]);

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (triggerRef.current?.contains(target)) return;
      if (dropdownRef.current?.contains(target)) return;
      close();
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(prev => !prev)}
        aria-expanded={open}
        className="flex items-center gap-1.5 text-[11px] text-gray-400 hover:text-gray-700 transition-colors"
      >
        <TriggerIcon className="h-3 w-3" />
        {triggerLabel}
      </button>
      {open && resolvedContainer && dropdownPosition && createPortal(
        <div
          ref={dropdownRef}
          className="space-y-1.5 rounded-md border border-gray-200 bg-white p-1.5 shadow-lg"
          style={{
            position: dropdownPosition.fixed ? 'fixed' : 'absolute',
            left: dropdownPosition.left,
            width: dropdownPosition.width,
            top: dropdownPosition.top,
            bottom: dropdownPosition.bottom,
            zIndex: 100000,
          }}
        >
          <div className="flex items-center gap-1.5 border border-gray-200 rounded-md px-2 py-1.5">
            <Search className="h-3 w-3 text-gray-400 shrink-0" />
            <input
              ref={inputRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={searchPlaceholder}
              className="flex-1 text-xs bg-transparent outline-hidden placeholder:text-gray-400"
            />
            <button type="button" onClick={close} className="text-gray-400 hover:text-gray-600">
              <X className="h-3 w-3" />
            </button>
          </div>
          {filtered.length > 0 && (
            <div
              className="divide-y divide-gray-100 overflow-y-auto rounded-md border border-gray-200"
              style={{ maxHeight: dropdownPosition.maxHeight }}
            >
              {filtered.map(c => (
                <button
                  type="button"
                  key={c.id}
                  onClick={() => { onPick(c); close(); }}
                  className="w-full text-left flex items-center gap-2 px-2.5 py-1.5 hover:bg-gray-50 transition-colors"
                >
                  {showEntityIcon && <EntityIcon type={c.entity_type as 'person' | 'organization' | 'other' | undefined} size="sm" />}
                  <span className="text-xs text-gray-700 truncate">{c.name}</span>
                </button>
              ))}
            </div>
          )}
          {search && filtered.length === 0 && emptyMessage && (
            <p className="text-[11px] text-gray-400 px-1">{emptyMessage}</p>
          )}
        </div>,
        resolvedContainer,
      )}
    </>
  );
}
